import React, { useState, useEffect } from 'react';
import { Navigate, useSearchParams } from 'react-router-dom';
import { Building2, Users, MessageSquare, ClipboardList, SlidersHorizontal, Sparkles } from 'lucide-react';
import { usePermissions } from '../contexts/PermissionsContext';
import { PERMISSIONS } from '../utils/permissions';
import ClientManager from '../components/admin/ClientManager';
import UserManager from '../components/admin/UserManager';
import FeedbackManager from '../components/admin/FeedbackManager';
import OnboardingManager from '../components/admin/OnboardingManager';
import ConfigRequestsManager from '../components/admin/ConfigRequestsManager';
import ReleaseNotesManager from '../components/admin/ReleaseNotesManager';

const ALL_TABS = [
  { id: 'clients',        label: 'Clients',            icon: Building2,         requiresClientManage: true },
  { id: 'users',          label: 'Users',               icon: Users,             requiresClientManage: false },
  { id: 'onboarding',     label: 'Onboarding',          icon: ClipboardList,     requiresClientManage: true },
  { id: 'config_requests',label: 'Config Requests',     icon: SlidersHorizontal, requiresClientManage: true },
  { id: 'feedback',       label: 'Feedback & Faults',   icon: MessageSquare,     requiresClientManage: false },
  { id: 'release_notes',  label: "What's New",          icon: Sparkles,          requiresClientManage: true },
];

export default function AdminPanel() {
  useEffect(() => {
    document.title = 'Admin Panel — STX Connect';
  }, []);
  const { hasPermission } = usePermissions();
  const [searchParams] = useSearchParams();

  const canManageClients = hasPermission(PERMISSIONS.CLIENT_MANAGE);
  const canManageUsers   = hasPermission(PERMISSIONS.USER_MANAGE);
  const TABS = ALL_TABS.filter(t => !t.requiresClientManage || canManageClients);
  const defaultTab = canManageClients ? 'clients' : 'users';
  const initialTab = TABS.some(t => t.id === searchParams.get('tab'))
    ? searchParams.get('tab')
    : defaultTab;
  const initialId = searchParams.get('id') || null;

  const [tab, setTab] = useState(initialTab);

  if (!canManageClients && !canManageUsers) return <Navigate to="/dashboard" replace />;

  return (
    <div>
      <h1 className="text-2xl font-bold text-gray-800 mb-1">Admin Panel</h1>
      <p className="text-sm text-gray-700 mb-6">Manage client tenants and users across the platform.</p>

      <div className="flex gap-1 border-b border-gray-200 mb-6">
        {TABS.map(({ id, label, icon: Icon }) => (
          <button
            key={id}
            onClick={() => setTab(id)}
            className={`flex items-center gap-2 px-4 py-2.5 text-sm font-medium border-b-2 transition-colors ${
              tab === id
                ? 'border-blue-600 text-blue-600'
                : 'border-transparent text-gray-700 hover:text-gray-700'
            }`}
          >
            <Icon size={15} />
            {label}
          </button>
        ))}
      </div>

      {tab === 'clients'         && <ClientManager />}
      {tab === 'users'           && <UserManager />}
      {tab === 'onboarding'      && <OnboardingManager />}
      {tab === 'config_requests' && <ConfigRequestsManager />}
      {tab === 'feedback'        && <FeedbackManager initialId={initialId} />}
      {tab === 'release_notes'   && <ReleaseNotesManager />}
    </div>
  );
}
